import { Category, OfferType } from '@smg-real-estate/hg-rets-schema';

import { topCategories, TopCategory } from '@/helpers/listingCategoryGroups';
import { Insertion } from '@/types';

const rentalFeatures = ['PETS_ALLOWED', 'SMOKING_PERMITTED', 'WASHING_MACHINE'];

const featuresFieldsVisibilityMapping: { [index: string]: Category[] } = {
  WHEELCHAIR_ACCESS: [
    ...topCategories['APPT'],
    ...topCategories['HOUSE'],
    ...topCategories['SECONDARY'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'],
  ].filter((item) => ![Category.Garage, Category.ParkingSpace].includes(item)),
  CHILD_FRIENDLY: [...topCategories['APPT'], ...topCategories['HOUSE']],
  PETS_ALLOWED: [...topCategories['APPT'], ...topCategories['HOUSE'], ...topCategories['INDUS']],
  SMOKING_PERMITTED: [...topCategories['APPT'], ...topCategories['HOUSE']],
  BALCONY_TERRACE: [...topCategories['APPT'], ...topCategories['HOUSE'], ...topCategories['INDUS']],
  GARAGE: [
    ...topCategories['APPT'],
    ...topCategories['HOUSE'],
    ...topCategories['SECONDARY'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'],
  ].filter((item) => ![Category.Garage, Category.ParkingSpace].includes(item)),
  PARKING_PLACE: [
    ...topCategories['APPT'],
    ...topCategories['HOUSE'],
    ...topCategories['SECONDARY'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'],
  ].filter((item) => ![Category.Garage, Category.ParkingSpace].includes(item)),
  ELEVATOR: [
    ...topCategories['APPT'],
    ...topCategories['SECONDARY'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'],
  ].filter((item) => ![Category.Garage, Category.ParkingSpace].includes(item)),
  FIREPLACE: [...topCategories['APPT'], ...topCategories['HOUSE']],
  WASHING_MACHINE: [...topCategories['APPT'], ...topCategories['HOUSE']],
  DISHWASHER: [...topCategories['APPT'], ...topCategories['HOUSE']],
  VIEW: [
    ...topCategories['APPT'],
    ...topCategories['HOUSE'],
    ...topCategories['PROP'],
    ...topCategories['INDUS'],
  ],
  QUITE_NEIGHBOURHOOD: [
    ...topCategories['APPT'],
    ...topCategories['HOUSE'],
    ...topCategories['PROP'],
  ],
  MINERGIE_CONSTRUCTION: [
    ...topCategories['APPT'],
    ...topCategories['HOUSE'],
    ...topCategories['INDUS'],
  ],
  MINERGIE_CERTIFIED: [
    ...topCategories['APPT'],
    ...topCategories['HOUSE'],
    ...topCategories['INDUS'],
  ],
  NEW_BUILDING: [
    ...topCategories['APPT'],
    ...topCategories['HOUSE'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'],
  ].filter((item) => ![Category.Garage, Category.ParkingSpace].includes(item)),
  OLD_BUILDING: [
    ...topCategories['APPT'],
    ...topCategories['HOUSE'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'],
  ].filter((item) => ![Category.Garage, Category.ParkingSpace].includes(item)),
  CABLE_TV: [...topCategories['APPT'], ...topCategories['HOUSE'], ...topCategories['INDUS']],
  GROUND_FLOOR_RAISED: [
    ...topCategories['APPT'],
    ...topCategories['HOUSE'],
    ...topCategories['INDUS'],
    ...topCategories['SECONDARY'],
  ],
  SWIMMING_POOL: [...topCategories['APPT'], ...topCategories['HOUSE']],
  FLAT_SHARING_COMMUNITY: [...topCategories['APPT'], ...topCategories['HOUSE']],
  BUILDING_LAW_RESTRICTIONS: [...topCategories['PROP']],
  LORRY_RAMP: [...topCategories['INDUS']],
  LIFTING_PLATFORM: [...topCategories['INDUS']],
  TOILETS: [...topCategories['INDUS']],
  WATER_SUPPLY: [...topCategories['INDUS']],
  SEWAGE_DISPOSAL_CONNECTION: [...topCategories['INDUS']],
  POWER_SUPPLY: [...topCategories['INDUS']],
  FLOOR_LOAD: [...topCategories['INDUS']],
  CRANE_LIFTING_CAPACITY: [...topCategories['INDUS']],
  GOODS_LIFT_LOADING_CAPACITY: [...topCategories['INDUS']],
  ROOMS: [
    ...topCategories['APPT'],
    ...topCategories['HOUSE'],
    ...topCategories['SECONDARY'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'].filter(
      (item) => ![Category.Garage, Category.ParkingSpace].includes(item),
    ),
  ],
  NUMBER_OF_BATHROOMS: [...topCategories['APPT'], ...topCategories['HOUSE']],
  LIVING_SPACE: [
    ...topCategories['APPT'],
    ...topCategories['HOUSE'],
    ...topCategories['SECONDARY'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'].filter(
      (item) => ![Category.Garage, Category.ParkingSpace].includes(item),
    ),
  ],
  FLOOR: [
    ...topCategories['APPT'],
    ...topCategories['SECONDARY'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'].filter(
      (item) => ![Category.Garage, Category.ParkingSpace].includes(item),
    ),
  ],
  NUMBER_OF_FLOORS: [
    ...topCategories['HOUSE'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'].filter(
      (item) => ![Category.Garage, Category.ParkingSpace].includes(item),
    ),
    Category.Duplex,
  ],
  LOT_SIZE: [
    ...topCategories['HOUSE'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'].filter(
      (item) => ![Category.Garage, Category.ParkingSpace].includes(item),
    ),
    ...topCategories['PROP'],
  ],
  MIN_FLOOR_SPACE: [...topCategories['INDUS']],
  FLOOR_SPACE: [
    ...topCategories['APPT'],
    ...topCategories['HOUSE'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'],
    ...topCategories['PARK'],
  ],
  ROOM_HEIGHT: [
    ...topCategories['HOUSE'],
    ...topCategories['APPT'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'].filter(
      (item) => ![Category.Garage, Category.ParkingSpace].includes(item),
    ),
    Category.UndergroundSlot,
  ],
  VOLUME: [...topCategories['HOUSE'], ...topCategories['APPT'], ...topCategories['INDUS']],
  CRANE_CAPACITY: [...topCategories['INDUS']],
  ELEVATOR_CAPACITY: [...topCategories['INDUS']],
  YEAR_BUILT: [
    ...topCategories['APPT'],
    ...topCategories['HOUSE'],
    ...topCategories['SECONDARY'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'],
    ...topCategories['PARK'],
  ],
  LAST_RENOVATED: [
    ...topCategories['APPT'],
    ...topCategories['HOUSE'],
    ...topCategories['SECONDARY'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'].filter(
      (item) => ![Category.Garage, Category.ParkingSpace].includes(item),
    ),
  ],
  VIDEO: [
    ...topCategories['APPT'],
    ...topCategories['HOUSE'],
    ...topCategories['PROP'],
    ...topCategories['SECONDARY'],
    ...topCategories['GASTRO'],
    ...topCategories['AGRI'],
    ...topCategories['INDUS'],
    ...topCategories['PARK'],
  ],
};

const featureToCharacteristicMapping: { [index: string]: string } = {
  WHEELCHAIR_ACCESS: 'isWheelchairAccessible',
  CHILD_FRIENDLY: 'isChildFriendly',
  PETS_ALLOWED: 'arePetsAllowed',
  SMOKING_PERMITTED: 'isSmokingAllowed',
  BALCONY_TERRACE: 'hasBalcony',
  GROUND_FLOOR_RAISED: 'isGroundFloorRaised',
  GARAGE: 'hasGarage',
  PARKING_PLACE: 'hasParking',
  ELEVATOR: 'hasElevator',
  SWIMMING_POOL: 'hasSwimmingPool',
  FIREPLACE: 'hasFireplace',
  WASHING_MACHINE: 'hasWashingMachine',
  DISHWASHER: 'hasDishwasher',
  VIEW: 'hasNiceView',
  QUITE_NEIGHBOURHOOD: 'isQuiet',
  MINERGIE_CONSTRUCTION: 'isMinergieGeneral',
  MINERGIE_CERTIFIED: 'isMinergieCertified',
  OLD_BUILDING: 'isOldBuilding',
  FLAT_SHARING_COMMUNITY: 'hasFlatSharingCommunity',
  CABLE_TV: 'hasCableTv',
  LORRY_RAMP: 'hasRamp',
  LIFTING_PLATFORM: 'hasLiftingPlatform',
  WATER_SUPPLY: 'hasWaterSupply',
  SEWAGE_DISPOSAL_CONNECTION: 'hasSewageSupply',
  POWER_SUPPLY: 'hasPowerSupply',
  BUILDING_LAW_RESTRICTIONS: 'hasBuildingLawRestrictions',
  NEW_BUILDING: 'isNewBuilding',
  ROOMS: 'numberOfRooms',
  LIVING_SPACE: 'livingSpace',
  FLOOR: 'floor',
  NUMBER_OF_FLOORS: 'numberOfFloors',
  LOT_SIZE: 'lotSize',
  MIN_FLOOR_SPACE: 'singleFloorSpace',
  FLOOR_SPACE: 'totalFloorSpace',
  ROOM_HEIGHT: 'ceilingHeight',
  VOLUME: 'cubage',
  FLOOR_LOAD: 'floorLoad',
  CRANE_CAPACITY: 'craneCapacity',
  ELEVATOR_CAPACITY: 'elevatorCapacity',
  YEAR_BUILT: 'yearBuilt',
  LAST_RENOVATED: 'yearLastRenovated',
  NUMBER_OF_BATHROOMS: 'numberOfBathrooms',
};

export const featuresToShow = (categories: Category[], offerType: OfferType): string[] => {
  return Object.keys(featuresFieldsVisibilityMapping)
    .filter((field) =>
      featuresFieldsVisibilityMapping[field].some((category: any) => categories.includes(category)),
    )
    .filter((field) => (OfferType.Buy === offerType ? !rentalFeatures.includes(field) : field));
};

export const characteristicsForFeatures = (features: string[]): string[] => {
  return features.map((feature) => featureToCharacteristicMapping[feature]);
};

export const allFeatures = Object.keys(featuresFieldsVisibilityMapping);

export function clearInapplicableCharacteristics(state: Insertion, categories: Category[]): void {
  const offerType = state.listing.offerType || OfferType.Rent;
  const oldFeatures = featuresToShow(state.listing.categories || [], offerType);
  const newFeatures = featuresToShow(categories, offerType);
  if (newFeatures.length === 0) {
    const possibleCategories = topCategories[categories[0].toString() as TopCategory] || [];
    const possibleFeatures = featuresToShow(possibleCategories, offerType);
    newFeatures.push(...possibleFeatures);
  }
  const featuresToRemove = oldFeatures.filter((feature) => !newFeatures.includes(feature));
  const characteristics = characteristicsForFeatures(featuresToRemove);
  characteristics.forEach((characteristic) => {
    delete (state.listing.characteristics as any)[characteristic];
  });
}
